import { Grid } from '@mui/material'
import { notifyManager, useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import AppContext from '../../context/appContext'
import { useCarbonInstances } from '../../hooks/data/useCarbonInstances'
import { useCarbonInstancesGroupedByMonitoringPeriod } from '../../hooks/data/useCarbonInstancesGroupedByMonitoringPeriod'
import { ELIGIBILITY_QUERY, useEligibilityCheck } from '../../hooks/data/useEligibilityCheck'
import { queryKeys } from '../../hooks/data/queryKeys'
import { useExclusionAreas } from '../../hooks/data/useExclusionAreas'
import { useFarmSubdivisions } from '../../hooks/data/useFarmSubdivisions'
import { usePaddocks } from '../../hooks/data/usePaddocks'
import { useSamplingAreas } from '../../hooks/data/useSamplingAreas'
import { useMonitoringSites } from '../../hooks/data/useMonitoringSites'
import { useOtherPolygons } from '../../hooks/data/useOtherPolygons'
import { useOtherSites } from '../../hooks/data/useOtherSites'
import { useMapDimensions } from '../../hooks/useMapDimensions'
import {
  getStoredDrawingType,
  setStoredDrawingType,
  setStoredMapeoDone,
} from '../../services/appPreferences/farmMappingStorage'
import CategorizationPanel from '../formPanel/CategorizationPanel'
import MapLayered from '../map/MapLayered'

// A staged KML import or a drawing in progress: a background refetch of the farm row must not
// move the panel off these stages.
const EDITING_STAGES = new Set([
  'fileUploaded',
  'showingFoldersArchivo',
  'inicialManual',
  'drawingStarted',
])

const FarmMapping = ({
  actualYearId,
  handleActualYearId,
  saved,
  handleSaved,
  fileType,
  handleFileType,
  includeSoilInfo,
  handleIncludeSoilInfo,
  includeProjectArea,
  handleIncludeProjectArea,
  handleDownload,
}) => {
  const [drawingType, setDrawingType] = useState(getStoredDrawingType)
  const [center, setCenter] = useState([-35.329970415341656, -65.12262446073629])
  const [etapa, setEtapa] = useState('inicial')
  const etapaRef = useRef(etapa)
  etapaRef.current = etapa

  const { currentFarm } = useContext(AppContext)

  const fetchedPerimeter = useMemo(
    () => (currentFarm?.geometry ? [currentFarm.toGeoJSON] : null),
    [currentFarm?.geometry, currentFarm?.toGeoJSON],
  )
  const { data: farmSubdivisions } = useFarmSubdivisions({ farmId: currentFarm?.id })
  const { data: allPaddocks } = usePaddocks({ farmId: currentFarm?.id })
  const fetchedPaddocks = useMemo(() => {
    if (!farmSubdivisions || !allPaddocks || !actualYearId) return null
    const paddockIds = farmSubdivisions.find(fs => fs.id === actualYearId)?.paddockIds ?? []
    return allPaddocks
      .filter(paddock => paddockIds.includes(paddock.id))
      .map(paddock => paddock.toGeoJSON)
  }, [farmSubdivisions, allPaddocks, actualYearId])

  // undefined = untouched (the server layer shows), null = cleared, array = an import, a drawing
  // or edited folders. Not `??`: the cancel/reset paths need an empty layer, never the server one.
  const [importedPerimeter, setImportedPerimeter] = useState(undefined)
  const [importedPaddocks, setImportedPaddocks] = useState(undefined)
  const perimeter = importedPerimeter === undefined ? fetchedPerimeter : importedPerimeter
  const paddocks = importedPaddocks === undefined ? fetchedPaddocks : importedPaddocks

  const [unassigned, setUnassigned] = useState(null)
  const [unassignedPoints, setUnassignedPoints] = useState(null)

  const { data: fetchedOtherPolygons } = useOtherPolygons({ farmId: currentFarm?.id })
  const [importedOtherPolygons, setImportedOtherPolygons] = useState(null)
  const otherPolygons = importedOtherPolygons ?? fetchedOtherPolygons ?? null

  const { data: fetchedOtherSites } = useOtherSites({ farmId: currentFarm?.id })
  const [importedOtherSites, setImportedOtherSites] = useState(null)
  const otherSites = importedOtherSites ?? fetchedOtherSites ?? null

  const [mapUpdate, setMapUpdate] = useState(0)

  const { mapWidth, mapHeight } = useMapDimensions()

  const { carbonInstances, setCarbonInstances } = useCarbonInstances({
    farmId: currentFarm?.id,
    yearId: actualYearId,
  })
  const { groups: ciGroups } = useCarbonInstancesGroupedByMonitoringPeriod({
    farmId: currentFarm?.id,
  })
  const { eligibilityCheckAreas } = useEligibilityCheck({
    farmId: currentFarm?.id,
    query: ELIGIBILITY_QUERY,
  })
  const { data: fetchedSamplingAreas } = useSamplingAreas({
    farmId: currentFarm?.id,
  })
  // Same local-overlay pattern as monitoringSites below: a KML import shows its drafts
  // until saveFarmMapping persists them. The drafts are features while the cache holds
  // entities, so they must not be written into the query cache.
  const [importedSamplingAreas, setImportedSamplingAreas] = useState(null)
  const samplingAreas = importedSamplingAreas ?? fetchedSamplingAreas ?? null
  const queryClient = useQueryClient()
  const { data: fetchedExclusionAreas } = useExclusionAreas({
    farmId: currentFarm?.id,
  })
  const [importedExclusionAreas, setImportedExclusionAreas] = useState(null)
  const exclusionAreas = importedExclusionAreas ?? fetchedExclusionAreas ?? null
  const { data: rawMonitoringSites } = useMonitoringSites({ farmId: currentFarm?.id })
  // Transform to GeoJSON; child components may call handleMonitoringSites to overlay
  // locally-imported files until the full FarmMapping RQ migration moves all writes to mutations.
  const [importedMonitoringSites, setImportedMonitoringSites] = useState(null)
  const monitoringSites = useMemo(() => {
    if (importedMonitoringSites) return importedMonitoringSites
    return (
      rawMonitoringSites?.map(site => ({
        type: 'Feature',
        properties: {
          name: site.name,
          id: site.id,
          type: 'monitoringSite',
          featureGroup: 'monitoringSites',
        },
        // API returns [lat, lng]; GeoJSON expects [lng, lat]
        geometry: { type: 'Point', coordinates: [site.location[1], site.location[0]] },
      })) ?? null
    )
  }, [rawMonitoringSites, importedMonitoringSites])

  const handleDrawingType = type => {
    setDrawingType(type)
    setStoredDrawingType(type)
  }

  const handleCenter = newCenter => {
    setCenter(newCenter)
  }

  const handleEtapa = newEtapa => {
    setEtapa(newEtapa)
  }

  // A saved draft whose post-save refetch failed yields to the next successful fetch of the reads
  // it replaces; editing the drafts or switching farms makes them unsaved again and ends the wait.
  const stopWaitingForReadsRef = useRef(null)
  const stopWaitingForReads = useCallback(() => {
    stopWaitingForReadsRef.current?.()
    stopWaitingForReadsRef.current = null
  }, [])

  const handlePerimeter = newPerimeter => {
    stopWaitingForReads()
    setImportedPerimeter(newPerimeter)
  }

  const handlePaddocks = newPaddocks => {
    stopWaitingForReads()
    setImportedPaddocks(newPaddocks)
  }

  // A saved draft stays on screen until the reads that replace it have been refetched, so the map
  // never falls back to pre-save geometry; when the refetch fails the draft stays and the global
  // error toast reports it.
  const refetchReads = queryKeysToRead =>
    Promise.all(
      queryKeysToRead.map(queryKey =>
        queryClient.refetchQueries(
          { queryKey, exact: true, type: 'active' },
          { throwOnError: true },
        ),
      ),
    ).then(
      () => true,
      () => false,
    )

  // Scheduled behind the query notifications, so the refetched farm row reaches currentFarm before
  // the draft goes.
  const dropDraftsAfterNotifications = dropDrafts => notifyManager.schedule(dropDrafts)

  const settleSavedDrafts = async (queryKeysToRead, dropDrafts) => {
    stopWaitingForReads()
    const savedAt = Date.now()
    if (await refetchReads(queryKeysToRead)) {
      dropDraftsAfterNotifications(dropDrafts)
      return
    }
    const queryCache = queryClient.getQueryCache()
    const readsLanded = () =>
      queryKeysToRead.every(queryKey => {
        const query = queryCache.find({ queryKey, exact: true })
        return !query?.isActive() || query.state.dataUpdatedAt >= savedAt
      })
    stopWaitingForReadsRef.current = queryCache.subscribe(event => {
      if (event.type !== 'updated' || event.action.type !== 'success' || !readsLanded()) return
      stopWaitingForReads()
      dropDraftsAfterNotifications(dropDrafts)
    })
  }

  // The stage moves explicitly: the farm row was refetched while the drawing stage blocked the
  // promotion, so no later row change is left to promote it.
  const handlePerimeterSaved = () =>
    settleSavedDrafts([queryKeys.farm(currentFarm?.id)], () => {
      setImportedPerimeter(undefined)
      setEtapa('showingFoldersApi')
      setStoredMapeoDone(false)
    })

  const handleMappingSaved = () => {
    setEtapa('showingFoldersApi')
    setStoredMapeoDone(false)
    const farmId = currentFarm?.id
    return settleSavedDrafts(
      [queryKeys.farm(farmId), queryKeys.paddocks(farmId), queryKeys.farmSubdivisions(farmId)],
      () => {
        setImportedPerimeter(undefined)
        setImportedPaddocks(undefined)
      },
    )
  }

  const handleSamplingAreas = newSamplingAreas => {
    setImportedSamplingAreas(newSamplingAreas)
  }

  // saveFarmMapping turns the KML drafts into entities, so the overlay has to go and the
  // shared query must refetch: Monitoreo gates its Plan tab on this same cache and would
  // otherwise keep reading the pre-save list for the rest of its staleTime.
  const handleSamplingAreasSaved = () => {
    setImportedSamplingAreas(null)
    queryClient.invalidateQueries({ queryKey: queryKeys.samplingAreas(currentFarm?.id) })
  }

  const handleMonitoringSites = newMonitoringSites => {
    setImportedMonitoringSites(newMonitoringSites)
  }

  const handleUnassigned = newUnassigned => {
    setUnassigned(newUnassigned)
  }

  const handleUnassignedPoints = newUnassignedPoints => {
    setUnassignedPoints(newUnassignedPoints)
  }

  const handleExclusionAreas = newExclusionAreas => {
    // ShowFolders edits a row with an updater so the siblings hydrated in the same pass survive.
    // Resolving it against the fetched list keeps an edit on an API-loaded area from collapsing
    // the map into an empty overlay.
    setImportedExclusionAreas(current =>
      typeof newExclusionAreas === 'function'
        ? newExclusionAreas(current ?? fetchedExclusionAreas ?? null)
        : newExclusionAreas,
    )
  }

  const handleExclusionAreasSaved = () => {
    setImportedExclusionAreas(null)
  }

  const handleOtherPolygons = newOtherPolygons => {
    setImportedOtherPolygons(newOtherPolygons)
  }

  const handleOtherSites = newOtherSites => {
    setImportedOtherSites(newOtherSites)
  }

  // Same contract as handleSamplingAreasSaved: saveFarmMapping persisted the KML drafts as
  // entities, so the overlays must go and both shared queries refetch. Without this a remount
  // within the staleTime redraws the pre-save map.
  const handleOtherLayersSaved = () => {
    setImportedOtherPolygons(null)
    setImportedOtherSites(null)
    queryClient.invalidateQueries({ queryKey: queryKeys.otherPolygons(currentFarm?.id) })
    queryClient.invalidateQueries({ queryKey: queryKeys.otherSites(currentFarm?.id) })
  }

  const handleMapUpdate = () => {
    setMapUpdate(prev => prev + 1)
  }

  useEffect(() => {
    setImportedPerimeter(undefined)
    setImportedPaddocks(undefined)
    return stopWaitingForReads
  }, [currentFarm?.id, stopWaitingForReads])

  // Deliberate: picking another loteo drops a staged paddocks import. The first pick is not a
  // switch: ShowFolders auto-picks the loteo just saved, whose lots may still be the held drafts.
  const previousYearIdRef = useRef(actualYearId)
  useEffect(() => {
    const previousYearId = previousYearIdRef.current
    previousYearIdRef.current = actualYearId
    if (previousYearId && previousYearId !== actualYearId) setImportedPaddocks(undefined)
  }, [actualYearId])

  // Reads the stage through a ref so that only a farm row change, never a stage change, promotes.
  useEffect(() => {
    if (!currentFarm?.geometry || EDITING_STAGES.has(etapaRef.current)) return
    setEtapa('showingFoldersApi')
    setStoredMapeoDone(false)
  }, [currentFarm])

  // Center
  useEffect(() => {
    if (currentFarm?.lng && currentFarm?.lat) {
      setCenter([currentFarm.lat, currentFarm.lng])
    }
  }, [currentFarm?.lat, currentFarm?.lng])

  return (
    <Grid container>
      <Grid item xs={4}>
        <CategorizationPanel
          actualYearId={actualYearId}
          carbonInstances={carbonInstances}
          center={center}
          etapa={etapa}
          exclusionAreas={exclusionAreas}
          fileType={fileType}
          handleActualYearId={handleActualYearId}
          handleCarbonInstances={setCarbonInstances}
          handleCenter={handleCenter}
          handleDownload={handleDownload}
          handleDrawingType={handleDrawingType}
          handleEtapa={handleEtapa}
          handleExclusionAreas={handleExclusionAreas}
          handleExclusionAreasSaved={handleExclusionAreasSaved}
          handleFileType={handleFileType}
          handleIncludeProjectArea={handleIncludeProjectArea}
          handleIncludeSoilInfo={handleIncludeSoilInfo}
          handleMappingSaved={handleMappingSaved}
          handleMapUpdate={handleMapUpdate}
          handleMonitoringSites={handleMonitoringSites}
          handleOtherLayersSaved={handleOtherLayersSaved}
          handleOtherPolygons={handleOtherPolygons}
          handleOtherSites={handleOtherSites}
          handlePaddocks={handlePaddocks}
          handlePerimeter={handlePerimeter}
          handlePerimeterSaved={handlePerimeterSaved}
          handleSamplingAreas={handleSamplingAreas}
          handleSamplingAreasSaved={handleSamplingAreasSaved}
          handleSaved={handleSaved}
          handleUnassigned={handleUnassigned}
          handleUnassignedPoints={handleUnassignedPoints}
          includeProjectArea={includeProjectArea}
          includeSoilInfo={includeSoilInfo}
          mapUpdate={mapUpdate}
          monitoringSites={monitoringSites}
          otherPolygons={otherPolygons}
          otherSites={otherSites}
          paddocks={paddocks}
          perimeter={perimeter}
          samplingAreas={samplingAreas}
          saved={saved}
          unassigned={unassigned}
          unassignedPoints={unassignedPoints}
        />
      </Grid>
      <Grid item xs={8}>
        <MapLayered
          carbonInstancesByMonitoringPeriod={ciGroups}
          center={center}
          drawingType={drawingType}
          eligibilityAreas={eligibilityCheckAreas}
          etapa={etapa}
          exclusionAreas={exclusionAreas}
          handleEtapa={handleEtapa}
          handleExclusionAreas={handleExclusionAreas}
          handleMapUpdate={handleMapUpdate}
          handleMonitoringSites={handleMonitoringSites}
          handleOtherPolygons={handleOtherPolygons}
          handleOtherSites={handleOtherSites}
          handlePaddocks={handlePaddocks}
          handlePerimeter={handlePerimeter}
          handleSamplingAreas={handleSamplingAreas}
          handleUnassigned={handleUnassigned}
          handleUnassignedPoints={handleUnassignedPoints}
          mapHeight={mapHeight}
          mapUpdate={mapUpdate}
          mapWidth={mapWidth}
          monitoringSites={monitoringSites}
          otherPolygons={otherPolygons}
          otherSites={otherSites}
          paddocks={paddocks}
          perimeter={perimeter}
          samplingAreas={samplingAreas}
          unassigned={unassigned}
          unassignedPoints={unassignedPoints}
        />
      </Grid>
    </Grid>
  )
}

export default FarmMapping
